import React, { useContext } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid,
  TextField,
  Tooltip,
  Stack,
  DialogTitle,
  Divider,
} from '@mui/material';
import { staticFileLocation } from '@server/constants';
import { useForm, Controller } from 'react-hook-form';
import { HelpOutline } from '@mui/icons-material';
import { authContext, Position } from '@staffedupusa/shared-components';
import StaffLevelCopy from './StaffLevelCopy';
import { bold, medium } from '@styles/fontWeights';
import { useUpdateJobPosting } from '@features/jobPostings/hooks/useUpdateJobPosting';
import AutoApplicationsAction from '@features/employerOnboarding/components/HiringAutomation/AutoApplicationsAction';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import { autoAdjustHiringStatusToolTip } from '../constants/constants';
import StaffPercentPosition from './StaffPercentPosition';

type SetStaffLevelsModalProps = {
  open: boolean;
  handleClose: () => void;
  positions: [] | Position[];
};

type FormValues = {
  positions: {
    desired_staff_count: number;
    current_staff_count: number;
  }[];
};

const SetStaffLevelsModal: React.FC<SetStaffLevelsModalProps> = ({
  open,
  handleClose,
  positions,
}) => {
  const { authData } = useContext(authContext);
  const [jobPostings, setJobPostings] = React.useState<Position[]>(
    positions.sort((a, b) => a.s_title.localeCompare(b.s_title)),
  );
  const { updateJobPosting } = useUpdateJobPosting({ refetch: true });
  const { updateProfile } = useUpdateProfile({});
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      positions: jobPostings.map(() => ({
        desired_staff_count: 0,
        current_staff_count: 0,
      })),
    },
  });
  const totalStatff = jobPostings
    .filter((item) => item.status !== 4)
    .reduce(
      (acc, pos) => {
        acc.totalCurrent += Number(pos.staff_count.current_staff_count) || 0;
        acc.totalDesired += Number(pos.staff_count.desired_staff_count) || 0;
        return acc;
      },
      { totalCurrent: 0, totalDesired: 0 },
    );

  const handleInputChange = (
    id: number,
    field: 'desired_staff_count' | 'current_staff_count',
    value: number,
  ) => {
    setJobPostings((prev) =>
      prev.map((pos) =>
        pos.id === id
          ? {
              ...pos,
              staff_count: {
                ...pos.staff_count,
                [field]: value,
              },
            }
          : pos,
      ),
    );

    updateJobPosting({
      id,
      [field]: value,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 0 }}>
        <Box
          sx={{
            pt: 4,
            textAlign: 'center',
          }}
        >
          <img
            src={`${staticFileLocation}img/logos/LogoStampShadow.png`}
            alt="Logo"
            style={{
              width: 60,
              height: 80,
              objectFit: 'contain',
              margin: '0 auto',
            }}
          />
          <Typography
            variant="h3"
            fontWeight={900}
            color="primary.main"
            mt={2}
            mb={1}
          >
            My Team
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            mb={3}
            alignItems="center"
          >
            <StaffPercentPosition totalStatff={totalStatff} />
            <Tooltip
              placement="right"
              arrow
              title={
                <span
                  dangerouslySetInnerHTML={{
                    __html: ` <strong>Fully Staffed Par</strong> is the number of team members you need in each position to be fully staffed. <br /> <br /><strong>Current staff levels</strong> are the number of team members you have in each position.`,
                  }}
                />
              }
            >
              <HelpOutline fontSize="small" />
            </Tooltip>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 4 }}>
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="space-between"
          padding={2}
        >
          <Typography
            fontWeight={medium}
            variant="body1"
            sx={{ maxWidth: 'fit-content' }}
          >
            Teams Auto Adjust Hiring Status
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <AutoApplicationsAction
              field="auto_adjust_hiring_status"
              value={Boolean(
                authData?.user?.company?.check_list?.auto_adjust_hiring_status,
              )}
              updateProfile={(data) => {
                updateProfile(data);
              }}
            />
            <Tooltip
              title={
                <span
                  dangerouslySetInnerHTML={{
                    __html: autoAdjustHiringStatusToolTip,
                  }}
                />
              }
              placement="top"
              arrow
            >
              <HelpOutline color="secondary" fontSize="small" />
            </Tooltip>
          </Stack>
        </Stack>
        <Divider sx={{ my: '10px !important', width: '100%' }} />
        <Grid container spacing={2}>
          {jobPostings.map((pos, index) => (
            <Grid item xs={12} key={index}>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                px={2}
                py={1}
                borderBottom="1px solid #eee"
                gap={2}
              >
                {/* Staff count and title */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  minWidth={0} // prevents overflow
                >
                  <StaffLevelCopy position={pos} />
                  <Typography>{pos.s_title}</Typography>
                </Box>

                {/* Number inputs */}
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  {/* Par */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Controller
                      name={`positions.${index}.desired_staff_count`}
                      control={control}
                      rules={{ required: 'Required' }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Fully staffed par"
                          value={pos.staff_count.desired_staff_count}
                          type="text"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: { textAlign: 'center' },
                          }}
                          disabled={
                            !authData?.user?.master_account &&
                            authData?.user?.permissions &&
                            authData?.user?.permissions.restricted_positions.some(
                              (item) => item.id === pos.id,
                            )
                          }
                          onKeyDown={(e) => {
                            if (
                              !/[0-9]/.test(e.key) &&
                              ![
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                              ].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            const cleaned = target.value.replace(/\D/g, '');
                            target.value = cleaned;
                            field.onChange(cleaned);
                            handleInputChange(
                              pos.id as number,
                              'desired_staff_count',
                              Number(cleaned || 0),
                            );
                          }}
                          sx={{ width: { xs: '100%', sm: 150 } }}
                          size="small"
                        />
                      )}
                    />
                  </Box>

                  {/* Now */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Controller
                      name={`positions.${index}.current_staff_count`}
                      defaultValue={pos.staff_count.current_staff_count || 0}
                      control={control}
                      rules={{ required: 'Required' }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Current staff level"
                          type="text"
                          disabled={
                            !authData?.user?.master_account &&
                            authData?.user?.permissions &&
                            authData?.user?.permissions.restricted_positions.some(
                              (item) => item.id === pos.id,
                            )
                          }
                          value={pos.staff_count.current_staff_count}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: { textAlign: 'center' },
                          }}
                          onKeyDown={(e) => {
                            if (
                              !/[0-9]/.test(e.key) &&
                              ![
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                              ].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            const cleaned = target.value.replace(/\D/g, '');
                            target.value = cleaned;
                            field.onChange(cleaned);
                            handleInputChange(
                              pos.id as number,
                              'current_staff_count',
                              Number(cleaned || 0),
                            );
                          }}
                          sx={{ width: { xs: '100%', sm: 150 } }}
                          size="small"
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography textAlign="center" fontSize="0.875rem" color="gray" mt={4}>
          Add current staff & par levels to keep track of your hiring <br />{' '}
          efficiency & automate your hiring processes!
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default SetStaffLevelsModal;
